<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="d-flex align-items-center flex-wrap" v-if="attendanceData && attendanceData.allow_multi_day">
        <a-tooltip title="Toggle to edit attendance across all dates in this month">
          <a-switch
            v-model="multiDayMode"
            @change="handleMultiDayToggle"
            checked-children="Multi-Day Grid Edit ON"
            un-checked-children="Single Day Mode"
            class="mr-3 mb-1"
          />
        </a-tooltip>
        <template v-if="multiDayMode">
          <a-range-picker
            v-model="multiDaySubRange"
            format="YYYY-MM-DD"
            :disabled-date="disabledMultiDaySubRangeDate"
            :default-picker-value="[moment(attendanceDate), moment(attendanceDate)]"
            :placeholder="['Range Start', 'Range End']"
            size="small"
            class="mr-3 mb-1"
            allow-clear
          />
        </template>
      </div>
      <div v-else></div>
      <div class="text-right">
        <a-tag closable color="purple" v-if="infoVisible" class="m-0">
          <span class="py-2 pr-3">Attendance not yet submitted for the selected date: {{attendanceDate}}</span>
        </a-tag>
      </div>
    </div>
    <a-spin :spinning="loading">
      <div class="py-5 text-center" v-if="!attendanceData">
        <a-alert
          v-if="errorMessage"
          type="error"
          :message="errorMessage"
          show-icon
          class="d-inline-block text-left mb-3"
          style="max-width: 600px;"
        />
        <a-tag color="pink" v-else>Select class and section to view attendance details</a-tag>
      </div>
      <div v-else>
        <!-- Top Buttons -->
        <div class="row mb-2 mt-2" v-if="attendanceData">
          <div class="col-md-8">
            <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="handleMarkAllAbsent">
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-x" />
              </span>
              Mark all Absent
            </button>

            <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="handleMarkAllPresent">
              Mark all Present
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-check" />
              </span>
            </button>
            <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="handleClearAll">
              Reset
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-refresh-cw" />
              </span>
            </button>

            <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2"
              @click="handleAttendanceDateClick(attendanceDate)"
              :disabled="!attendanceData.students.length || moment(attendanceDate).day() === 0 || holidays.includes(attendanceDate)"
              v-if="!multiDayMode"
            >
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-grid" />
              </span>
              Add by Roll No
            </button>
            <button class="btn btn-sm btn-with-addon mb-2 "
                    :class="'btn-success'"
                    @click="handleApprove"
                    :disabled="attendanceData.is_approved ||!attendanceData.students.length || moment(attendanceDate).day() === 0 || holidays.includes(attendanceDate)"
                    v-if="rolesetting.approvable && attendanceData && attendanceData.attendance_exists && !multiDayMode"
            >
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-check-circle" />
              </span>
              {{attendanceData.is_approved ? 'Approved' : 'Approve Attendance' }}
            </button>
          </div>
          <div class="col-md-4 text-right text-md-right ">
            <h6 class="mb-2 text-dark font-weight-bold">Total School Days: {{ getTotalSchoolDays() }}</h6>
            <button class="btn btn-sm btn-with-addon mb-2 mr-2"
                    :class="attendanceData ? attendanceData.attendance_exists || multiDayMode ? 'btn-primary' : 'btn-success' : ''"
                    @click="handleSubmit"
                    :disabled="!attendanceData.students.length || (!multiDayMode && (moment(attendanceDate).day() === 0 || holidays.includes(attendanceDate)))"
                    v-if="rolesetting.insertable"
            >
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-check" />
              </span>
              {{ multiDayMode ? 'Update Multi-Day Attendance' : (attendanceData && attendanceData.attendance_exists ? 'Update Attendance' : 'Submit Attendance') }}
            </button>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered table-sm table-striped table-hover" :class="$style.attendanceTable">
          <thead>
            <tr>
              <th>#</th>
              <th>Name (Roll No.)</th>
              <th>Present</th>
              <th>Absent</th>
              <th>Total Days</th>
              <th v-for="date in attendanceData.dates" :key="date.date"
              :class="[$style.neoDateCell, date.date === attendanceDate ? 'neo-bg-light-blue' : '', (multiDayMode && hasActiveSubRange && isDateInSubRange(date.date)) ? 'subrange-active-header' : '', (multiDayMode && hasActiveSubRange && !isDateInSubRange(date.date)) ? 'subrange-dimmed' : '', (multiDayMode && !hasActiveSubRange && date.in_attendance && !date.is_weekend && date.date <= moment().format('YYYY-MM-DD')) ? 'bg-light-info neo-hover-pointer' : '', 'neo-hover-pointer']"
              @click="handleAttendanceDateClick(date.date)"
              >
                {{ moment(date.date).format('D') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(student, index) in attendanceData.students" :key="student.student_id">
              <th>{{ index + 1 }}</th>
              <td><div class="d-flex justify-content-between align-items-center">
                  <span :class="student.status === 0 ? 'text-danger' : ''">
                    {{
                      student.student.first_name +
                      " " +
                      student.student.last_name
                    }}
                    {{ student.roll_no ? `(${student.roll_no})` : "(NA)" }}
                    <span v-if="student.status === 0" class="font-weight-bold ml-1"> (inactive)</span>
                  </span>
                  <a-button
                    @click="openEditModal(student, attendanceDate)"
                    size="small"
                    type="link"
                    :disabled="student.status === 0"
                    v-if="!multiDayMode"
                  >
                    <i class="fe fe-edit" />
                  </a-button>
                </div></td>
              <td class="text-center font-weight-bold text-success">{{ getStudentSummary(student).present }}</td>
              <td class="text-center font-weight-bold text-danger">{{ getStudentSummary(student).absent }}</td>
              <td class="text-center font-weight-bold text-dark">{{ getStudentSummary(student).totalTaken }}</td>
              <td v-for="date in attendanceData.dates" :key="date.date"
                :class="[$style.neoMarkingCell, date.is_weekend ? 'bg-light' : '', !date.in_attendance ? 'bg-default' : '', date.date === attendanceDate ? 'neo-bg-light-blue' : '', (multiDayMode && hasActiveSubRange && isDateInSubRange(date.date) && date.in_attendance && !date.is_weekend && date.date <= moment().format('YYYY-MM-DD')) ? 'subrange-active-cell neo-hover-pointer' : '', (multiDayMode && hasActiveSubRange && !isDateInSubRange(date.date)) ? 'subrange-dimmed' : '', (multiDayMode && !hasActiveSubRange && date.in_attendance && !date.is_weekend && date.date <= moment().format('YYYY-MM-DD')) ? 'neo-hover-pointer' : '', student.status === 0 ? 'bg-light opacity-50' : '']"
                @click="handleCellClick(student.student_id, date, student.status)"
              >
                <p class="text-center m-0">
                  <template v-if="date.is_weekend || !date.in_attendance || date.date > moment().format('YYYY-MM-DD')">
                    <a-tag class="m-0 p-0" :class="$style.neoCellTagDisabled">
                      <i class="btn-addon-icon fe fe-hash" />
                    </a-tag>
                  </template>
                  <template v-else>
                    <a-tag
                      :color="getStudentStatusOnDate(student.student_id, date.date) === 'absent' ? 'red' : getStudentStatusOnDate(student.student_id, date.date) === 'present' ? 'green' : getStudentStatusOnDate(student.student_id, date.date) === 'leave' ? 'purple' : 'white'"
                      class="m-0 p-0"
                      :class="$style.neoCellTag">
                      <template v-if="getStudentStatusOnDate(student.student_id, date.date) === 'present'">
                        <i class="btn-addon-icon fe fe-check" />
                      </template>
                      <template v-else-if="getStudentStatusOnDate(student.student_id, date.date) === 'absent'">
                        <i class="btn-addon-icon fe fe-x" />
                      </template>
                      <template v-else-if="getStudentStatusOnDate(student.student_id, date.date) === 'leave'">
                        <small class="p-1 font-weight-bold">L</small>
                      </template>
                      <template v-else>
                        <i class="btn-addon-icon fe fe-square" />
                      </template>
                    </a-tag>
                  </template>
                </p>
              </td>
            </tr>
            <tr v-if="!attendanceData.students.length">
              <td :colspan="attendanceData.dates.length + 5">
                <no-data text="No Students" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="row mt-2" v-if="attendanceData">
        <div class="col-12">
          <div class=" border-top mb-2"></div>
        </div>
        <div class="col-md-8">
          <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="handleMarkAllAbsent">
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-x" />
            </span>
            Mark all Absent
          </button>

          <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="handleMarkAllPresent">
            Mark all Present
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-check" />
            </span>
          </button>
          <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="handleClearAll">
            Reset
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-refresh-cw" />
            </span>
          </button>

          <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2"
            @click="handleAttendanceDateClick(attendanceDate)"
            :disabled="!attendanceData.students.length || moment(attendanceDate).day() === 0 || holidays.includes(attendanceDate)"
            v-if="!multiDayMode"
          >
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-grid" />
            </span>
            Add by Roll No
          </button>
          <button class="btn btn-sm btn-with-addon mb-2 "
                  :class="'btn-success'"
                  @click="handleApprove"
                  :disabled="attendanceData.is_approved ||!attendanceData.students.length || moment(attendanceDate).day() === 0 || holidays.includes(attendanceDate)"
                  v-if="rolesetting.approvable && attendanceData && attendanceData.attendance_exists && !multiDayMode"
          >
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-check-circle" />
            </span>
            {{attendanceData.is_approved ? 'Approved' : 'Approve Attendance' }}
          </button>
        </div>
        <div class="col-md-4 text-right text-md-right ">
          <h6 class="mb-2 text-dark font-weight-bold">Total School Days: {{ getTotalSchoolDays() }}</h6>
          <button class="btn btn-sm btn-with-addon mb-2 mr-2"
                  :class="attendanceData ? attendanceData.attendance_exists || multiDayMode ? 'btn-primary' : 'btn-success' : ''"
                  @click="handleSubmit"
                  :disabled="!attendanceData.students.length || (!multiDayMode && (moment(attendanceDate).day() === 0 || holidays.includes(attendanceDate)))"
                  v-if="rolesetting.insertable"
          >
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-check" />
            </span>
            {{ multiDayMode ? 'Update Multi-Day Attendance' : (attendanceData && attendanceData.attendance_exists ? 'Update Attendance' : 'Submit Attendance') }}
          </button>
        </div>
      </div>
      </div>
    </a-spin>

    <!-- Add attendance by roll number -->
    <add-attendance ref="attendanceByRoll"
      :students="attendanceData ? attendanceData.students : []"
      :absentees="attendanceData ? attendanceData.absentees : []"
      @studentsSelected="handleBulkStudentSelect"
    />
    <a-modal
      :visible="editModalVisible"
      title="Edit Attendance"
      @cancel="closeEditModal"
      @ok="handleEditSubmit"
      :confirm-loading="editLoading"
    >
      <a-form layout="vertical">
        <!-- Row for Name and Roll No -->
        <div class="row mb-3">
          <div class="col">
            <strong>Name:</strong>
            {{
              selectedStudent && selectedStudent.student
                ? selectedStudent.student.first_name +
                  " " +
                  selectedStudent.student.last_name
                : ""
            }}
          </div>

          <div class="col">
             <strong>Student Code</strong>
            {{
              selectedStudent && selectedStudent.student
                ? selectedStudent.student.student_code
                : ""
            }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            <strong>Date:</strong>
            {{ selectedDate ? moment(selectedDate).format("DD-MM-YYYY") : "" }}
          </div>
          <div class="col">
           <strong>Roll No:</strong>
            {{
              selectedStudent && selectedStudent.roll_no
                ? selectedStudent.roll_no
                : "NA"
            }}
          </div>
        </div>

        <!-- Status Dropdown -->
        <a-form-item label="Status">
          <a-select v-model="editForm.status">
            <a-select-option :value="0">Present</a-select-option>
            <a-select-option :value="1">Absent</a-select-option>
            <a-select-option :value="2">Leave</a-select-option>
            <a-select-option :value="3">Pending</a-select-option>
          </a-select>
        </a-form-item>

        <!-- Row for Start and End Time -->
        <div class="row" v-if="editForm.status === 0">
          <div class="col-md-6">
            <a-form-item label="Start Time">
              <a-time-picker
                v-model="editForm.startTime"
                format="HH:mm"
                placeholder="Select start time"
                style="width: 100%"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item label="End Time">
              <a-time-picker
                v-model="editForm.endTime"
                format="HH:mm"
                placeholder="Select end time"
                style="width: 100%"
              />
            </a-form-item>
          </div>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>
<script>
import { includes } from 'lodash'
import moment from 'moment'
import AddAttendance from '@/components/attendance/Form/Add/index'

import NoData from '@/components/layout/NoData/index'

export default {
  name: 'AttendanceTable',
  components: {
    AddAttendance,
    NoData,
  },
  props: {
    attendanceData: Object,
    attendanceDate: String,
    additionalData: Object,
    rolesetting: { type: Object },
    errorMessage: { type: String, default: '' },
  },
  data: function() {
    return {
      infoVisible: false,
      loading: false,
      editLoading: false,
      editModalVisible: false,
      selectedStudent: null,
      editForm: {
        status: null,
        selectedDate: null,
        startTime: null,
        endTime: null,
      },
      selectedPresentStudents: [],
      selectedAbsentStudents: [],
      selectedLeaveStudents: [],
      holidays: [],
      classHasStudents: false,
      multiDayMode: false,
      multiDayState: {},
      multiDaySubRange: null,
    }
  },
  computed: {
    hasActiveSubRange() {
      return Boolean(this.multiDaySubRange && this.multiDaySubRange.length && this.multiDaySubRange[0] && this.multiDaySubRange[1])
    },
  },
  watch: {
    attendanceData: function (val) {
      this.initialze(val)
    },
  },
  methods: {
    moment,
    initialze: function (attendanceData) {
      if (!attendanceData) return
      this.selectedAbsentStudents = []
      this.holidays = attendanceData ? attendanceData.holidays.map(element => {
        if (!element.in_attendance) {
          return moment(element).format('YYYY-MM-DD')
        }
      }) : []
      this.selectedAbsentStudents = attendanceData ? attendanceData.absentees : []
      this.selectedLeaveStudents = attendanceData ? attendanceData.on_leave : []
      this.selectedPresentStudents = this.getPresentStudents(attendanceData, this.attendanceDate)
      this.infoVisible = attendanceData ? !attendanceData.attendance_exists && !includes(this.holidays, this.attendanceDate) : false
      this.initMultiDayState(attendanceData)
    },
    initMultiDayState(attendanceData) {
      if (!attendanceData || !attendanceData.dates) return
      const state = {}
      const todayStr = moment().format('YYYY-MM-DD')
      attendanceData.dates.forEach(dObj => {
        const dStr = dObj.date
        if (dObj.in_attendance && !dObj.is_weekend && dStr <= todayStr) {
          if (dStr === this.attendanceDate) {
            state[dStr] = {
              presents: [...this.selectedPresentStudents],
              absentees: [...this.selectedAbsentStudents],
              onLeave: [...this.selectedLeaveStudents],
            }
          } else {
            const presents = []
            const absentees = []
            const onLeave = []
            attendanceData.students.forEach(({ student }) => {
              if (!student || !student.attendance) return
              const rec = student.attendance.find(a => a.date === dStr)
              if (rec) {
                if (rec.status === 0) presents.push(student.id)
                else if (rec.status === 1) absentees.push(student.id)
                else if (rec.status === 2) onLeave.push(student.id)
              }
            })
            state[dStr] = { presents, absentees, onLeave }
          }
        }
      })
      this.multiDayState = state
    },
    handleMultiDayToggle(checked) {
      this.multiDayMode = checked
      this.multiDaySubRange = null
      if (checked && this.attendanceData) {
        this.initMultiDayState(this.attendanceData)
      }
    },
    disabledMultiDaySubRangeDate(current) {
      if (!this.attendanceDate || !current) return false
      const monthStart = moment(this.attendanceDate).startOf('month')
      const monthEnd = moment(this.attendanceDate).endOf('month')
      const today = moment().endOf('day')
      return current.isBefore(monthStart, 'day') || current.isAfter(monthEnd, 'day') || current.isAfter(today, 'day')
    },
    isDateInSubRange(dateStr) {
      if (!this.multiDaySubRange || !this.multiDaySubRange.length || !this.multiDaySubRange[0] || !this.multiDaySubRange[1]) {
        return true // Default to entire month if no sub-range selected
      }
      const start = this.multiDaySubRange[0].format('YYYY-MM-DD')
      const end = this.multiDaySubRange[1].format('YYYY-MM-DD')
      return dateStr >= start && dateStr <= end
    },
    getStudentStatusOnDate(studentId, dateStr) {
      if (this.multiDayMode && this.multiDayState[dateStr]) {
        const st = this.multiDayState[dateStr]
        if (st.presents.includes(studentId)) return 'present'
        if (st.absentees.includes(studentId)) return 'absent'
        if (st.onLeave && st.onLeave.includes(studentId)) return 'leave'
        return null
      }
      if (dateStr === this.attendanceDate) {
        if (this.isInSelectedPresentStudents(studentId)) return 'present'
        if (this.isInSelectedAbsentStudents(studentId)) return 'absent'
        if (this.isInSelectedLeaveStudents(studentId)) return 'leave'
        return null
      }
      if (!this.attendanceData || !this.attendanceData.students) return null
      const stud = this.attendanceData.students.find(s => s.student_id === studentId)
      if (stud && stud.student && stud.student.attendance) {
        return this.checkAttendanceStatus(stud.student.attendance, dateStr)
      }
      return null
    },
    getPendingStudentsForDate(dateStr) {
      if (!this.attendanceData || !this.attendanceData.students) return []
      const st = this.multiDayState[dateStr] || { presents: [], absentees: [], onLeave: [] }
      const pendingIds = []
      this.attendanceData.students.forEach(({ student_id: studentId }) => {
        if (
          !st.presents.includes(studentId) &&
          !st.absentees.includes(studentId) &&
          (!st.onLeave || !st.onLeave.includes(studentId))
        ) {
          pendingIds.push(studentId)
        }
      })
      return pendingIds
    },
    handleCellClick: function(studentId, date, status) {
      // Allow only selected date and active students
      if (status === 0) return
      const todayStr = moment().format('YYYY-MM-DD')

      if (this.multiDayMode) {
        if (!date.in_attendance || date.is_weekend || date.date > todayStr || !this.isDateInSubRange(date.date)) return

        if (!this.multiDayState[date.date]) {
          this.$set(this.multiDayState, date.date, { presents: [], absentees: [], onLeave: [] })
        }
        const state = this.multiDayState[date.date]
        const isPresent = state.presents.includes(studentId)
        const isAbsent = state.absentees.includes(studentId)
        const isLeave = state.onLeave ? state.onLeave.includes(studentId) : false

        if (!isPresent && !isAbsent && !isLeave) {
          state.presents.push(studentId)
        } else if (isPresent) {
          state.presents = state.presents.filter(id => id !== studentId)
          state.absentees.push(studentId)
        } else if (isAbsent) {
          state.absentees = state.absentees.filter(id => id !== studentId)
          if (!state.onLeave) state.onLeave = []
          state.onLeave.push(studentId)
        } else if (isLeave) {
          state.onLeave = state.onLeave.filter(id => id !== studentId)
        }
        return
      }

      if (date.in_attendance && date.date === this.attendanceDate) {
        const isPresent = this.selectedPresentStudents.includes(studentId)
        const isAbsent = this.selectedAbsentStudents.includes(studentId)
        const isLeave = this.selectedLeaveStudents.includes(studentId)

        if (!isPresent && !isAbsent && !isLeave) {
          // Mark as Present
          this.selectedPresentStudents.push(studentId)
        } else if (isPresent) {
          // Move from Present to Absent
          this.selectedPresentStudents = this.selectedPresentStudents.filter(id => id !== studentId)
          this.selectedAbsentStudents.push(studentId)
        } else if (isAbsent) {
          // Move from Absent to Leave
          this.selectedAbsentStudents = this.selectedAbsentStudents.filter(id => id !== studentId)
          this.selectedLeaveStudents.push(studentId)
        } else if (isLeave) {
          // Remove from Leave → back to None
          this.selectedLeaveStudents = this.selectedLeaveStudents.filter(id => id !== studentId)
        }
      }
    },
    openEditModal(student, date) {
      const attendanceRecord = student.student.attendance.find(
        (a) => a.date === this.attendanceDate
      )
      this.selectedDate = date
      let status
      let startTime = null
      let endTime = null

      if (attendanceRecord) {
        status = attendanceRecord.status
        startTime = attendanceRecord.start_time
          ? moment(attendanceRecord.start_time, 'HH:mm')
          : null
        endTime = attendanceRecord.end_time
          ? moment(attendanceRecord.end_time, 'HH:mm')
          : null
      } else {
        // No attendance record found, use selection fallback
        if (this.selectedAbsentStudents.includes(student.student_id)) {
          status = 1 // Absent
        } else if (this.selectedLeaveStudents.includes(student.student_id)) {
          status = 2 // Leave
        } else if (this.selectedPresentStudents.includes(student.student_id)) {
          status = 0 // Present
        } else {
          status = 3 // Pending
        }
      }

      this.selectedStudent = student
      this.editForm = {
        status,
        startTime,
        endTime,
        selectedDate: date,
      }
      this.editModalVisible = true
    },

    handleEditSubmit() {
      if (!this.selectedStudent) return

      const attendanceRecord = this.selectedStudent.student.attendance.find(
        a => a.date === this.attendanceDate
      )

      const attendanceId = attendanceRecord ? attendanceRecord.id : null

      const payload = attendanceId
        ? {
          attendance_id: attendanceId,
          status: this.editForm.status,
          start_time: this.editForm.startTime
            ? this.editForm.startTime.format('HH:mm')
            : null,
          end_time: this.editForm.endTime
            ? this.editForm.endTime.format('HH:mm')
            : null,
        }
        : {
          student_id: this.selectedStudent.student_id,
          date: this.attendanceDate,
          session_id: this.additionalData.sessionId,
          class_id: this.additionalData.classId,
          section_id: this.additionalData.sectionId,
          status: this.editForm.status,
          start_time: this.editForm.startTime
            ? this.editForm.startTime.format('HH:mm')
            : null,
          end_time: this.editForm.endTime
            ? this.editForm.endTime.format('HH:mm')
            : null,
        }

      this.editLoading = true

      this.$http
        .post('/api/attendance-single', payload)
        .then(() => {
          this.$notification.success({
            message: 'Success',
            description: 'Attendance updated successfully!',
          })
          this.editLoading = false
          this.editModalVisible = false
          this.selectedStudent = null
          this.$emit('submitted') // Reload attendance data
        })
        .catch(error => {
          this.editLoading = false
          this.$neoNotify.error(error)
        })
    },
    closeEditModal() {
      this.editModalVisible = false
      this.selectedStudent = null
    },

    checkAttendanceStatus(studentAttendance, date) {
      let status
      studentAttendance.forEach(element => {
        if (element.date === date && element.status === 2) status = 'leave'
        if (element.date === date && element.status === 1) status = 'absent'
        if (element.date === date && element.status === 0) status = 'present'
      })
      return status
    },
    isInSelectedPresentStudents (studentId) {
      return this.selectedPresentStudents.includes(studentId)
    },
    isInSelectedAbsentStudents (studentId) {
      return this.selectedAbsentStudents.includes(studentId)
    },
    isInSelectedLeaveStudents (studentId) {
      return this.selectedLeaveStudents.includes(studentId)
    },
    handleMarkAllAbsent () {
      if (this.multiDayMode) {
        const activeIds = this.attendanceData.students.filter(e => e.status !== 0).map(e => e.student_id)
        Object.keys(this.multiDayState).forEach(dStr => {
          if (this.isDateInSubRange(dStr)) {
            this.multiDayState[dStr].presents = []
            this.multiDayState[dStr].onLeave = []
            this.multiDayState[dStr].absentees = [...activeIds]
          }
        })
        return
      }
      this.selectedLeaveStudents = []
      this.selectedPresentStudents = []
      this.selectedAbsentStudents = this.attendanceData.students
        .filter(element => element.status !== 0)
        .map(element => {
          return element.student_id
        })
    },
    handleMarkAllPresent () {
      if (this.multiDayMode) {
        const activeIds = this.attendanceData.students.filter(e => e.status !== 0).map(e => e.student_id)
        Object.keys(this.multiDayState).forEach(dStr => {
          if (this.isDateInSubRange(dStr)) {
            this.multiDayState[dStr].absentees = []
            this.multiDayState[dStr].onLeave = []
            this.multiDayState[dStr].presents = [...activeIds]
          }
        })
        return
      }
      this.selectedAbsentStudents = []
      this.selectedLeaveStudents = []
      this.selectedPresentStudents = this.attendanceData.students
        .filter(element => element.status !== 0)
        .map(element => {
          return element.student_id
        })
    },
    handleClearAll () {
      if (this.multiDayMode) {
        Object.keys(this.multiDayState).forEach(dStr => {
          if (this.isDateInSubRange(dStr)) {
            this.multiDayState[dStr].absentees = []
            this.multiDayState[dStr].onLeave = []
            this.multiDayState[dStr].presents = []
          }
        })
        return
      }
      this.selectedLeaveStudents = []
      this.selectedPresentStudents = []
      this.selectedAbsentStudents = []
    },
    handleAttendanceDateClick(date) {
      if (this.attendanceDate === date && this.attendanceData.students.length !== 0) {
        // this.$refs.attendanceByRoll.showModal()
        this.$eventBus.$emit('openAttendanceAddByRollModal')
      }
      if (this.attendanceData.students.length === 0) {
        this.$message.warning('You cannot add attendance! No student found in this class!')
      }
    },
    handleBulkStudentSelect(studentIds) {
      this.selectedAbsentStudents = studentIds
      this.handleSubmit()
    },
    getPresentStudents(attendanceStudData, targetDate) {
      if (!attendanceStudData || !attendanceStudData.students) return []
      const students = attendanceStudData.students || []
      const presentIds = []
      students.forEach(({ student }) => {
        if (!student || !student.attendance) return

        const attendedOnDate = student.attendance.find(
          record => record.date === targetDate && record.status === 0 // PRESENT
        )

        if (attendedOnDate) {
          presentIds.push(student.id)
        }
      })

      return presentIds
    },
    getPendingStudents() {
      if (!this.attendanceData || !this.attendanceData.students) return []
      const pendingIds = []
      this.attendanceData.students.forEach(({ student_id: studentId }) => {
        if (
          !this.selectedPresentStudents.includes(studentId) &&
          !this.selectedAbsentStudents.includes(studentId) &&
          !this.selectedLeaveStudents.includes(studentId)
        ) {
          pendingIds.push(studentId)
        }
      })
      return pendingIds
    },
    handleApprove() {
      if (!this.holidays.includes(this.attendanceDate) && moment(this.attendanceDate).day() !== 0) {
        this.$confirm({
          title: `Are you sure?`,
          content: `Click Ok to approve attendance for ${moment(this.attendanceDate).format('DD-MM-YYYY')}`,
          onOk: () => {
            this.loading = true
            this.$http
              .post('/api/attendance/approve', {
                date: this.attendanceDate,
                session_id: this.additionalData.sessionId,
                class_id: this.additionalData.classId,
                section_id: this.additionalData.sectionId,
              })
              .then(({ data }) => {
                this.loading = false
                this.$notification['success']({
                  message: `Attendance approved successfully!`,
                  description: `Attendance for ${moment(this.attendanceDate).format('DD-MM-YYYY')} has been approved!`,
                })
                this.selectedAbsentStudents = []
                this.$emit('submitted', this.attendanceDate)
              })
              .catch(error => {
                this.loading = false
                this.$neoNotify.error(error)
                this.$emit('submitted', this.attendanceDate) // Trigger to reload the attendance table
              })
          },
          onCancel: () => {
            this.loading = false
          },
        })
      }
    },
    handleSubmit() {
      if (this.multiDayMode) {
        const records = []
        Object.keys(this.multiDayState).forEach(dateStr => {
          if (this.isDateInSubRange(dateStr)) {
            const state = this.multiDayState[dateStr]
            const pendings = this.getPendingStudentsForDate(dateStr)
            records.push({
              date: dateStr,
              presents: state.presents,
              absentees: state.absentees,
              on_leave: state.onLeave || [],
              pendings: pendings,
            })
          }
        })

        if (records.length === 0) {
          this.$message.warning('No valid dates available in the selected range for multi-day update!')
          return
        }

        const hasSubRange = this.multiDaySubRange && this.multiDaySubRange[0] && this.multiDaySubRange[1]
        const subRangeDesc = hasSubRange
          ? `from ${this.multiDaySubRange[0].format('DD-MM-YYYY')} to ${this.multiDaySubRange[1].format('DD-MM-YYYY')}`
          : `for the entire month`

        this.$confirm({
          title: `Submit Multi-Day Attendance?`,
          content: `Click Ok to update attendance for ${records.length} date(s) (${subRangeDesc}).`,
          onOk: () => {
            this.loading = true
            this.$http
              .post('/api/attendance/multi-day', {
                session_id: this.additionalData.sessionId,
                class_id: this.additionalData.classId,
                section_id: this.additionalData.sectionId,
                records: records,
              })
              .then(({ data }) => {
                this.loading = false
                this.$notification['success']({
                  message: `Multi-Day Attendance Submitted!`,
                  description: data.message || `Attendance for ${records.length} dates has been saved!`,
                })
                this.$emit('submitted', this.attendanceDate)
              })
              .catch(error => {
                this.loading = false
                this.$neoNotify.error(error)
              })
          },
          onCancel: () => {
            this.loading = false
          },
        })
        return
      }

      if (!this.holidays.includes(this.attendanceDate) && moment(this.attendanceDate).day() !== 0) {
        this.$confirm({
          title: `Are you sure?`,
          content: `Click Ok to submit attendance for ${moment(this.attendanceDate).format('DD-MM-YYYY')}`,
          onOk: () => {
            this.loading = true
            this.$http
              .post('/api/attendance', {
                date: this.attendanceDate,
                session_id: this.additionalData.sessionId,
                class_id: this.additionalData.classId,
                section_id: this.additionalData.sectionId,
                absentees: this.selectedAbsentStudents,
                on_leave: this.selectedLeaveStudents,
                presents: this.selectedPresentStudents,
                pendings: this.getPendingStudents(),
              })
              .then(({ data }) => {
                this.loading = false
                this.$notification['success']({
                  message: `Attendance submitted successfully!`,
                  description: `Attendance for ${moment(this.attendanceDate).format('DD-MM-YYYY')} has been submitted!`,
                })
                this.selectedAbsentStudents = []
                this.$emit('submitted', this.attendanceDate)
              })
              .catch(error => {
                this.loading = false
                this.$neoNotify.error(error)
              })
          },
          onCancel: () => {
            this.loading = false
          },
        })
      }
    },
    getTotalSchoolDays() {
      if (!this.attendanceData || !this.attendanceData.dates) return 0
      return this.attendanceData.dates.filter(d => d.in_attendance).length
    },
    getStudentSummary(student) {
      let present = 0
      let absent = 0
      let totalTaken = 0

      if (!this.attendanceData || !this.attendanceData.dates) {
        return { present: 0, absent: 0, totalTaken: 0 }
      }

      this.attendanceData.dates.forEach(dateObj => {
        const dateStr = dateObj.date
        const status = this.getStudentStatusOnDate(student.student_id, dateStr)
        if (status === 'present') {
          present++
          totalTaken++
        } else if (status === 'absent') {
          absent++
          totalTaken++
        } else if (status === 'leave') {
          totalTaken++
        }
      })

      return { present, absent, totalTaken }
    },
  },
}
</script>
<style lang="scss" module>
@import './style.modules.scss';
</style>
<style scoped>
/* Override default bootstrap hover color to be more distinct from zebra stripes */
.table-hover tbody tr:hover td,
.table-hover tbody tr:hover th {
  background-color: #e6f7ff !important;
}

/* Multi-Day Sub-Range Visual Highlight Styles */
.subrange-active-header {
  background-color: #1890ff !important;
  color: #ffffff !important;
  font-weight: bold !important;
}

.subrange-active-cell {
  background-color: #e6f7ff !important;
  border-left: 1px solid #91d5ff !important;
  border-right: 1px solid #91d5ff !important;
}

.subrange-dimmed {
  opacity: 0.35 !important;
  background-color: #f8f9fa !important;
}
</style>
